import { useEffect, useMemo, useState } from 'react'
import TableMui from 'src/components/TableMui'
import { capitalizeString } from 'src/utils/capitalize'
import { useSelector } from 'src/utils/store'
import { actions } from '../../TransfersState'
import { useDispatch } from 'react-redux'
import { DeleteOutline, OpenInNew } from '@mui/icons-material'
import { useHistory } from 'react-router-dom'
import * as S from './TransfersManagementTable.styles'
import ConfirmOrRejectModal from 'src/features/base/components/ConfirmOrRejectModal'
import { toast } from 'react-toastify'
import { Typography } from '@mui/material'
import { TableErrorsLog } from '../../TransfersTypes'
import ModalTransferEmptyCage from '../ModalTransferEmptyCage/ModalTransferEmptyCage'

const TransfersManagementTable = () => {
  const {
    transfersLists, selectedTransfersLists, lastGetTransfersListsPayload, tableTransfersListsPagination,
  } = useSelector((state) => state.transfers)

  const [errorsLog, setErrorsLog] = useState<TableErrorsLog[]>([])

  const history = useHistory()
  const dispatch = useDispatch()

  const [modalRemoveTransferList, setModalRemoveTransferList] = useState({
    show: false,
    idList: -1,
  })
  const [showModalIssueInvoices, setShowModalIssueInvoices] = useState(false)
  const [preventDoubleClick, setPreventDoubleClick] = useState(false)

  const [showModalTransferEmptyCage, setShowModalTransferEmptyCage] = useState(false)

  const setSelectedTransfersLists = (value: number[]) => {
    dispatch(actions.setProperty({ property: 'selectedTransfersLists', value }))
  }

  const getTransfersListsLastPayload = (page?: number) => {
    dispatch(actions.getTransfersLists({
      ...lastGetTransfersListsPayload,
      page: page ?? tableTransfersListsPagination.currentPage,
      perPage: tableTransfersListsPagination.perPage,
      order: tableTransfersListsPagination.order,
      orderBy: tableTransfersListsPagination.orderBy,
    }))
  }

  useEffect(() => {
    if (transfersLists.length > 0) {
      setSelectedTransfersLists([])
      getTransfersListsLastPayload(1)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleOpenList = (idList: number) => {
    history.push(`/transfers/manage/${idList}/pages`)
  }

  const handleRemoveList = (idList?: number) => {
    if (preventDoubleClick) {
      return
    }
    if (!modalRemoveTransferList.show && idList) {
      setModalRemoveTransferList({
        show: true,
        idList: idList!,
      })
    } else if (modalRemoveTransferList.show && modalRemoveTransferList.idList !== -1) {
      setPreventDoubleClick(true)

      const onError = () => {
        setPreventDoubleClick(false)
      }

      const onSuccess = () => {
        setPreventDoubleClick(false)
        setSelectedTransfersLists([])
        setModalRemoveTransferList({ show: false, idList: -1 })
        toast.success('Lista de transferência excluída ou cancelada com sucesso!')
        getTransfersListsLastPayload()
      }

      dispatch(actions.removeTransferList({
        idList: modalRemoveTransferList.idList,
        onError,
        onSuccess,
      }))
    }
  }

  const tableColumns = [
    { id: 'type', label: 'Tipo', testId: 'type-column-test-id' },
    { id: 'sourceFc', label: 'FC de origem', testId: 'source-fc-column-test-id' },
    { id: 'destinationFc', label: 'FC de destino', testId: 'destination-fc-column-test-id' },
    {
      id: 'shippingDate', label: 'Data de expedição', testId: 'shipping-date-column-test-id', isDate: true, dateFormat: 'DD/MM/YYYY',
    },
    { id: 'priority', label: 'Prioridade', testId: 'priority-column-test-id' },
    {
      id: 'startedAt', label: 'Data de início', testId: 'started-at-column-test-id', isDate: true,
    },
    {
      id: 'finishedAt', label: 'Data de finalização', testId: 'finished-at-column-test-id', isDate: true,
    },
    { id: 'createdBy', label: 'Criado por', testId: 'created-by-column-test-id' },
    {
      id: 'createdAt', label: 'Criado em', testId: 'created-at-column-test-id', isDate: true,
    },
    {
      id: 'status', label: 'Status', testId: 'status-column-test-id',
    },
    {
      id: 'pagesFinishedVsTotal', label: 'Pg finalizadas / total', testId: 'pages-finished-vs-total-column-test-id', useSorting: false,
    },
    {
      id: 'open', label: 'Abrir', testId: 'open-column-test-id', useSorting: false,
    },
    {
      id: 'remove', label: 'Remover', testId: 'remove-column-test-id', useSorting: false,
    },
  ]

  const resolveTableRows = useMemo(() => transfersLists.map((transfer) => ({
    id: transfer.id,
    type: transfer.type,
    sourceFc: transfer.sourceFc,
    destinationFc: transfer.destinationFc,
    shippingDate: transfer.shippingDate,
    priority: transfer.priority,
    startedAt: transfer.startedAt,
    finishedAt: transfer.finishedAt,
    createdBy: capitalizeString(transfer.createdBy),
    createdAt: transfer.createdAt,
    status: capitalizeString(transfer.status),
    pagesFinishedVsTotal: transfer.pagesFinishedVsTotal,
    open: (
      <OpenInNew
        sx={{ cursor: 'pointer' }}
        onClick={(event) => {
          event.stopPropagation()
          handleOpenList(transfer.id)
        }}
        data-testid='open-list-button-test-id'
      />
    ),
    remove: (
      <DeleteOutline
        sx={{ color: 'red', cursor: 'pointer' }}
        onClick={(event) => {
          event.stopPropagation()
          handleRemoveList(transfer.id)
        }}
        data-testid='remove-list-button-test-id'
      />
    ),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  })), [transfersLists])

  const tableErrorsLogColumns = [
    {
      id: 'idList', label: 'ID da Lista', testId: 'id-list-column-test-id', useSorting: false,
    },
    {
      id: 'message', label: 'Mensagem', testId: 'message-column-test-id', useSorting: false,
    },
  ]

  const resolveTableRowsErrorsLog = useMemo(() => errorsLog.map((error, index) => ({
    id: index,
    idList: error.id,
    message: error.message,
  })), [errorsLog])

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const selectedIds = resolveTableRows.map((row) => row.id)
      setSelectedTransfersLists(selectedIds)
      return
    }
    setSelectedTransfersLists([])
  }

  const handleCheckboxClick = (id: number) => {
    const selectedIndex = selectedTransfersLists.findIndex((selectedTransferListId) => selectedTransferListId === id)

    if (selectedIndex === -1) {
      const transferList = transfersLists.find((transfer) => transfer.id === id)

      if (transferList) {
        setSelectedTransfersLists([...selectedTransfersLists, transferList.id])
      }
    } else {
      const updatedSelectedIds = selectedTransfersLists.filter((selectedTransferListId) => selectedTransferListId !== id)
      setSelectedTransfersLists(updatedSelectedIds)
    }
  }

  const getTransfersLists = (page: number, perPage: number, order?: string, orderBy?: string | number) => {
    dispatch(actions.setProperty({
      property: 'tableTransfersListsPagination',
      value: {
        ...tableTransfersListsPagination,
        currentPage: page,
        perPage,
        order,
        orderBy,
      },
    }))

    if (!transfersLists || transfersLists.length === 0) {
      return
    }

    setSelectedTransfersLists([])

    dispatch(actions.getTransfersLists({
      ...lastGetTransfersListsPayload,
      page,
      perPage,
      order,
      orderBy,
    }))
  }

  const handleDownloadInvoices = () => {
    if (selectedTransfersLists.length === 0 || preventDoubleClick) {
      return
    }
    setPreventDoubleClick(true)
    setErrorsLog([])

    const onSuccess = (errors: TableErrorsLog[]) => {
      setPreventDoubleClick(false)
      setErrorsLog(errors)
      if (errors.length === 0) {
        toast.success('Notas baixadas com sucesso!')
      } else {
        toast.info('Algumas notas apresentaram erros ao baixar. Confira o log de erros abaixo.')
      }
    }

    dispatch(actions.downloadTransfersListsInvoices({
      idsLists: selectedTransfersLists,
      onError: () => setPreventDoubleClick(false),
      onSuccess,
    }))
  }

  const handleIssueInvoices = () => {
    if (selectedTransfersLists.length === 0 || preventDoubleClick) {
      return
    }
    if (!showModalIssueInvoices) {
      setShowModalIssueInvoices(true)
    } else {
      setPreventDoubleClick(true)
      setErrorsLog([])

      const onError = () => {
        setPreventDoubleClick(false)
      }

      const onSuccess = (errors: TableErrorsLog[]) => {
        setPreventDoubleClick(false)
        setShowModalIssueInvoices(false)
        setErrorsLog(errors)
        if (errors.length === 0) {
          toast.success('Notas enviadas para emissão com sucesso!')
        } else {
          toast.info('Algumas listas/notas apresentaram erros ao enviar para emissão. Confira o log de erros abaixo.')
        }
        getTransfersListsLastPayload()
      }

      dispatch(actions.issueTransfersListsInvoices({
        idsLists: selectedTransfersLists,
        onError,
        onSuccess,
      }))
    }
  }

  return (
    <>
      <S.Wrapper>
        <TableMui
          columns={tableColumns}
          emptyLabel='Utilize o filtro para buscar as transferências'
          rows={resolveTableRows}
          rowCount={resolveTableRows.length}
          enablePagination
          rowsPerPageOptions={[5, 10, 15, 20, 25, 30]}
          minHeight='54vh'
          maxHeight='54vh'
          hasSorting
          hasCheckbox
          selected={selectedTransfersLists}
          selectedCount={selectedTransfersLists.length}
          checkboxSelectAllTooltipText='Ao selecionar, apenas as transferências dessa página serão selecionadas'
          onRowCheckboxClick={handleCheckboxClick}
          onSelectAllClick={handleSelectAllClick}
          getPaginatedItems={getTransfersLists}
          rowsPerPageLimit={tableTransfersListsPagination.perPage}
          totalPages={tableTransfersListsPagination.totalPages}
          currentPage={tableTransfersListsPagination.currentPage - 1}
          totalItems={tableTransfersListsPagination.totalItems}
          testId='transfers-lists-table-test-id'
        />
        <S.ContainerButtons>
          <S.SubContainerButtons>
            <S.CustomButton
              title='Baixar Notas'
              type='button'
              onClick={handleDownloadInvoices}
              bgColor='blue'
              hoverColor='darkBlueHover'
              letterColor='white'
              testId='download-invoices-button-test-id'
              disabled={selectedTransfersLists.length === 0 || preventDoubleClick}
            />
            <S.CustomButton
              title='Emitir Notas'
              type='button'
              onClick={handleIssueInvoices}
              bgColor='confirm'
              hoverColor='confirmHover'
              letterColor='white'
              testId='issue-invoices-button-test-id'
              disabled={selectedTransfersLists.length === 0 || preventDoubleClick}
            />
          </S.SubContainerButtons>
          <S.CustomButton
            title='Transferir Gaiola Vazia'
            type='button'
            onClick={() => setShowModalTransferEmptyCage(true)}
            bgColor='blue'
            hoverColor='darkBlueHover'
            letterColor='white'
            testId='transfer-empty-cage-button-test-id'
          />
        </S.ContainerButtons>
        <Typography
          sx={{
            display: 'flex',
            width: '100%',
            justifyContent: 'center',
          }}
          variant='h6'
          fontWeight='bold'
        >
          Log de Erros
        </Typography>
        <TableMui
          columns={tableErrorsLogColumns}
          emptyLabel='Nenhum erro a ser exibido'
          rows={resolveTableRowsErrorsLog}
          rowCount={resolveTableRowsErrorsLog.length}
          enablePagination
          rowsPerPageOptions={[5, 10, 15, 20, 25, 30]}
          rowsPerPageLimit={10}
          minHeight='54vh'
          maxHeight='54vh'
          testId='errors-log-table-test-id'
        />
      </S.Wrapper>
      <ConfirmOrRejectModal
        show={modalRemoveTransferList.show}
        title='Remover lista de transferência'
        description='Deseja realmente excluir ou cancelar a lista de transferência?'
        onConfirm={handleRemoveList}
        onClose={() => setModalRemoveTransferList({ show: false, idList: -1 })}
        onReject={() => setModalRemoveTransferList({ show: false, idList: -1 })}
        dataTestId='modal-remove-transfer-list-test-id'
      />
      <ConfirmOrRejectModal
        show={showModalIssueInvoices}
        title='Emitir notas'
        description='Deseja realmente emitir notas para as listas de transferência selecionadas?'
        onConfirm={handleIssueInvoices}
        onClose={() => setShowModalIssueInvoices(false)}
        onReject={() => setShowModalIssueInvoices(false)}
        dataTestId='modal-issue-invoices-test-id'
      />
      <ModalTransferEmptyCage
        show={showModalTransferEmptyCage}
        onClose={() => setShowModalTransferEmptyCage(false)}
      />
    </>
  )
}

export default TransfersManagementTable
